import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { CheckCircle, Circle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReservationChecklist = ({ reservationId }) => {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChecklist();
  }, [reservationId]);

  const fetchChecklist = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/pre-reservations/${reservationId}/checklist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChecklist(response.data);
    } catch (error) {
      console.error('Error fetching checklist:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = async (itemId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(
        `${API}/pre-reservations/${reservationId}/checklist/${itemId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setChecklist(response.data.checklist);
      toast.success('Checklist updated');
    } catch (error) {
      console.error('Error toggling checklist item:', error);
      toast.error('Failed to update checklist');
    }
  };

  if (loading) {
    return (
      <Card className="mt-4">
        <CardContent className="py-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            <p className="mt-2 text-sm text-gray-600">Loading checklist...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!checklist) {
    return null;
  }

  const completedCount = checklist.items.filter(item => item.completed).length;
  const totalCount = checklist.items.length;
  const progress = (completedCount / totalCount) * 100;

  return (
    <Card className="mt-4 border-2" style={{ borderColor: '#62D84E' }}>
      <CardHeader className="bg-green-50">
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center">
            <span className="text-2xl mr-2">✅</span>
            Next Steps Checklist
          </span>
          <span className="text-sm font-normal text-gray-600">
            {completedCount} / {totalCount} completed
          </span>
        </CardTitle>
        {/* Progress Bar */}
        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
          <div
            className="h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%`, backgroundColor: '#62D84E' }}
          />
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
          <p className="text-sm font-semibold text-blue-900 mb-2">
            🎯 A Collaboration has been created!
          </p>
          <p className="text-sm text-blue-800 mb-3">
            Your collaboration is now tracked in the Collaborations section where you can manage contracts, timesheets, and project lifecycle.
          </p>
          <a
            href="/collaborations"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <span className="mr-2">→</span>
            Go to Collaborations
          </a>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Follow these steps to successfully onboard and start the mission:
        </p>
        <div className="space-y-3">
          {checklist.items.map((item) => (
            <div
              key={item.id}
              className={`flex items-start p-3 rounded-lg border-2 transition-colors cursor-pointer ${
                item.completed
                  ? 'bg-green-50 border-green-200 hover:bg-green-100'
                  : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}
              onClick={() => toggleItem(item.id)}
            >
              <div className="flex-shrink-0 mt-0.5">
                {item.completed ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <Circle className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <div className="ml-3 flex-1">
                <p className={`text-sm font-medium ${
                  item.completed ? 'text-green-900 line-through' : 'text-gray-900'
                }`}>
                  {item.label}
                </p>
                {item.completed && item.completed_at && (
                  <p className="text-xs text-green-700 mt-1">
                    Completed: {new Date(item.completed_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            💡 <strong>Tip:</strong> Click on any item to mark it as complete. Both parties can see and update this checklist.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReservationChecklist;
